import React, { useMemo, useState } from 'react'
import { ParentIcon } from '../SVGs'
import { useMap } from '../IndoorProvider'
import { LabelContainer, MarkerContainer } from './styles'
import { isRackType, getDeviceColors } from '../../../../pages/Supervision/helpers'

const WIBOX_TYPES = ['wibox', 'wifirst-server']

const ParentLabel = ({ device }) => {
  const { iconSize, convertPosRealToMap, transformWrapperState } = useMap()

  const text = useMemo(() => {
    if (device.label.length > 9) {
      return `${device.label.slice(0, 4)}...${device.label.slice(
        device.label.length - 4,
      )}`
    }
    return device.label
  }, [device.label])

  const sizes = useMemo(() => {
    const { x, y } = convertPosRealToMap(device)
    const textWidth = text.length * 6
    const padding = 16
    const rectWidth = textWidth + padding
    const rectHeight = 20
    const totalWidth = rectWidth + 8
    const totalHeight = 28
    
    return {
      svg: {
        viewBox: `0 0 ${totalWidth} ${totalHeight}`,
        x: x - (totalWidth / 2) / transformWrapperState.scale + 25 / transformWrapperState.scale,
        y: y + (iconSize * 1.1) / transformWrapperState.scale,
        width: totalWidth / transformWrapperState.scale,
        height: totalHeight / transformWrapperState.scale,
      },
      rect: {
        x: 4,
        y: 4,
        width: rectWidth,
        height: rectHeight,
        centerX: totalWidth / 2,
        centerY: totalHeight / 2
      }
    }
  }, [device, transformWrapperState.scale, iconSize, text])

  return (
    <LabelContainer {...sizes.svg}>
      <rect
        x={sizes.rect.x}
        y={sizes.rect.y}
        width={sizes.rect.width}
        height={sizes.rect.height}
        rx={10}
        ry={10}
        fill="#292929"
        filter="drop-shadow(1px 2px 2px rgba(0, 0, 0, .16))"
      />
      <text
        x={sizes.rect.centerX}
        y={sizes.rect.centerY}
        fontSize="10px"
        fill="white"
        strokeWidth={0.4}
        textAnchor="middle"
        alignmentBaseline="middle"
      >
        {text}
      </text>
    </LabelContainer>
  )
}

const ParentMarker = ({
  device,
  selected,
  onMarkerEnter = () => {},
  onMarkerLeave = () => {},
  onMarkerClick = () => {},
}) => {
  const [hovered, setHovered] = useState(false)
  const { convertPosRealToMap, iconSize, transformWrapperState } = useMap()

  const position = useMemo(() => {
    const { x, y } = convertPosRealToMap(device)
    return { x, y }
  }, [device, transformWrapperState.scale])

  const markerData = useMemo(() => ({
    ...device,
    id: device.uuid,
    uuid: device.uuid,
    color: '#a99683'
  }), [device])

  const handleMarkerEnter = () => {
    setHovered(true)
    onMarkerEnter(markerData)
  }

  const handleMarkerLeave = () => {
    setHovered(false)
    onMarkerLeave()
  }

  const handleMarkerClick = () => {
    onMarkerClick?.(device)
  }

  const colors = useMemo(() => {
    const isRack = isRackType(device.model?.type)
    if (isRack) {
      return {
        default: { bgcolor: '#a99683', bordercolor: '#464646', color: '#a99683' },
        hover: { bgcolor: '#b5a690', bordercolor: '#464646', color: '#b5a690' },
        active: { bgcolor: '#8e7d6f', bordercolor: '#464646', color: '#8e7d6f' }
      }
    } else {
      return getDeviceColors(device)
    }
  }, [device])

  const isRack = isRackType(device.model?.type)
  const viewBoxSize = isRack ? iconSize * 4 : iconSize * 2.5
  const isWiboxGroup = WIBOX_TYPES.includes(device.model?.type) || device.children?.some(child => WIBOX_TYPES.includes(child.model?.type))
  const connectedDevices = isWiboxGroup ? device.children?.length || 0 : device.connected_stations || 0

  return (
    <>
      <MarkerContainer
        key={`${device.uuid}`}
        id={device.uuid}
        onClick={handleMarkerClick}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        width={(iconSize / transformWrapperState.scale) * (isRack ? 4 : 2.5)}
        height={(iconSize / transformWrapperState.scale) * (isRack ? 4 : 2.5)}
        {...position}
      >
        <ParentIcon
          iconSize={iconSize}
          selected={selected}
          pulse={selected}
          hovered={hovered}
          colors={colors}
          connectedDevices={connectedDevices}
          deviceType={device.model?.type}
          device={device}
          onMouseEnter={handleMarkerEnter}
          onMouseLeave={handleMarkerLeave}
        />
      </MarkerContainer>
      <ParentLabel device={device} />
    </>
  )
}

export default ParentMarker